import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { PhoneInput } from '../ui/PhoneInput';
import { AddressBlock, AddressValue, EMPTY_ADDRESS, isAddressValid } from '../ui/AddressBlock';
import { Checkbox } from '../ui/Checkbox';
import { PASSPORT_COUNTRIES } from '../../constants/countries';
import { validateEmail, validateName, validatePhoneMY, validateDob, validateMalaysianId, validatePassport, formatNric, isCivilianNricFormat } from '../../constants/validators';
import { DemoFillBanner } from '../ui/DemoFillBanner';
import { SAMPLE_PERSONAL_INFO } from '../../constants/sampleData';
import { cleanPhoneMY } from '../../constants/validators';
interface PersonalInfoJourneyProps {
  onComplete: (data: any) => void;
  onExit: () => void;
  prefillEmail?: string;
  prefillName?: string;
  prefillPhone?: string;
  /** Registered business address from Step 1, offered as "Same as registered". */
  businessAddress?: AddressValue;
  initialData?: any;
}
type IdMethod = 'national_id' | 'passport';
export function PersonalInfoJourney({
  onComplete,
  onExit,
  prefillEmail = '',
  prefillName = '',
  prefillPhone = '',
  businessAddress,
  initialData
}: PersonalInfoJourneyProps) {
  const [fullName, setFullName] = useState(initialData?.fullName ?? prefillName);
  const [email, setEmail] = useState(initialData?.email ?? prefillEmail);
  const [dob, setDob] = useState(initialData?.dob ?? '');
  const [phone, setPhone] = useState(cleanPhoneMY(initialData?.phone ?? prefillPhone ?? ''));
  // "Same as Registered Business Address" — checked by default when a business
  // address is available and the user hasn't yet entered a different home address.
  const hasBusinessAddress = Boolean(businessAddress && isAddressValid(businessAddress));
  const [sameAsBusiness, setSameAsBusiness] = useState<boolean>(initialData?.sameAsBusiness ?? hasBusinessAddress);
  const [address, setAddress] = useState<AddressValue>(initialData?.address ?? (hasBusinessAddress && !initialData ? businessAddress as AddressValue : EMPTY_ADDRESS));
  // Keep address in sync with the toggle: copy from business when checked,
  // restore to a blank slate when unchecked (so user can edit fresh).
  useEffect(() => {
    if (sameAsBusiness && hasBusinessAddress) {
      setAddress(businessAddress as AddressValue);
    }
  }, [sameAsBusiness, hasBusinessAddress, businessAddress]);
  const [idMethod, setIdMethod] = useState<IdMethod>(initialData?.idMethod ?? 'national_id');
  const [idNumber, setIdNumber] = useState(initialData?.idNumber ?? '');
  const [passportCountry, setPassportCountry] = useState(initialData?.passportCountry ?? '');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Step management
  type Step = 'personal' | 'identity' | 'address';
  const [currentStep, setCurrentStep] = useState<Step>('personal');
  // Today as max for DOB picker
  const todayIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  // ---------- Field-level errors (centralized validators) ----------
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    const nameErr = validateName(fullName);
    if (nameErr) e.fullName = nameErr;
    const emailErr = validateEmail(email);
    if (emailErr) e.email = emailErr;
    const dobErr = validateDob(dob);
    if (dobErr) e.dob = dobErr;
    const phoneErr = validatePhoneMY(phone);
    if (phoneErr) e.phone = phoneErr;
    if (idMethod === 'national_id') {
      const idErr = validateMalaysianId(idNumber, {
        dob
      });
      if (idErr) e.idNumber = idErr;
    } else {
      const passErr = validatePassport(idNumber);
      if (passErr) e.idNumber = passErr;
      if (!passportCountry) e.passportCountry = 'Select the issuing country of your passport.';
    }
    if (!isAddressValid(address)) e.address = 'Complete all required address fields.';
    return e;
  }, [fullName, email, dob, phone, idMethod, idNumber, passportCountry, address]);
  const isPersonalValid = !errors.fullName && !errors.email && !errors.dob && !errors.phone && fullName.trim() !== '' && email.trim() !== '' && dob !== '' && phone.trim() !== '';
  const isIdentityValid = !errors.idNumber && !errors.passportCountry && idNumber.trim() !== '' && (idMethod === 'national_id' || passportCountry !== '');
  const isAddressStepValid = !errors.address && isAddressValid(address);
  const isCurrentStepValid = () => {
    if (currentStep === 'personal') return isPersonalValid;
    if (currentStep === 'identity') return isIdentityValid;
    if (currentStep === 'address') return isAddressStepValid;
    return false;
  };
  const showError = (key: string): string | undefined => submitAttempted ? errors[key] : undefined;
  const handleNext = () => {
    setSubmitAttempted(true);
    if (!isCurrentStepValid()) return;
    if (currentStep === 'personal') {
      setSubmitAttempted(false);
      setCurrentStep('identity');
    } else if (currentStep === 'identity') {
      setSubmitAttempted(false);
      setCurrentStep('address');
    } else {
      handleSubmit();
    }
  };
  const handleBack = () => {
    if (currentStep === 'personal') {
      onExit();
    } else if (currentStep === 'identity') {
      setCurrentStep('personal');
    } else {
      setCurrentStep('identity');
    }
  };
  const handleSubmit = () => {
    onComplete({
      fullName,
      email,
      dob,
      phone,
      address,
      sameAsBusiness,
      idMethod,
      idNumber,
      passportCountry: idMethod === 'passport' ? passportCountry : undefined
    });
  };
  const handleNricChange = (raw: string) => {
    if (idMethod === 'national_id' && isCivilianNricFormat(raw)) {
      setIdNumber(formatNric(raw));
    } else {
      // Police/Military — preserve user input (alphanumeric, uppercased)
      setIdNumber(raw.toUpperCase().slice(0, 15));
    }
  };
  const handleIdMethodChange = (m: IdMethod) => {
    setIdMethod(m);
    setIdNumber('');
    setPassportCountry('');
  };
  return <div className="flex flex-col h-full max-w-xl mx-auto pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-205">
      <button onClick={handleBack} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors mb-6 self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded" data-id="element-206">
        <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-207" />
        {currentStep === 'personal' ? 'Back to Dashboard' : 'Back'}
      </button>

      {/* Header */}
      <div className="mb-6" data-id="element-208">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-209">
          {currentStep === 'personal' && 'Personal Information'}
          {currentStep === 'identity' && 'Identity Details'}
          {currentStep === 'address' && 'Home Address'}
        </h1>
        <p className="text-gray-600" data-id="element-210">
          {currentStep === 'personal' && 'Please provide the personal details of the business owner, primary director, or authorized representative.'}
          {currentStep === 'identity' && 'We need to verify your identity to comply with Bank Negara Malaysia regulations.'}
          {currentStep === 'address' && 'Please provide your residential address.'}
        </p>
      </div>

      <DemoFillBanner className="mb-8" onFill={() => {
      setFullName(SAMPLE_PERSONAL_INFO.fullName);
      setEmail(SAMPLE_PERSONAL_INFO.email);
      setDob(SAMPLE_PERSONAL_INFO.dob);
      setPhone(SAMPLE_PERSONAL_INFO.phone);
      setAddress(SAMPLE_PERSONAL_INFO.address);
      setIdMethod(SAMPLE_PERSONAL_INFO.idMethod);
      setIdNumber(SAMPLE_PERSONAL_INFO.idNumber);
      setPassportCountry(SAMPLE_PERSONAL_INFO.passportCountry);
    }} data-id="element-211" />

      <form onSubmit={e => {
      e.preventDefault();
      handleNext();
    }} noValidate className="space-y-10 flex-1 pb-28" data-id="element-212">
        {currentStep === 'personal' && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-213">
            {/* Personal Information */}
            <FormGroup title="Personal Information" data-id="element-214">
              <Input label="Full Legal Name" placeholder="e.g. Mohammad Ali @ Ah Meng" value={fullName} onChange={e => setFullName(e.target.value)} helperText="Must match your official government ID. Symbols @ / - ' are accepted for Malaysian names." error={showError('fullName')} required data-id="element-215" />
              <Input label="Email Address" type="email" inputMode="email" autoComplete="email" placeholder="e.g. ahmad@example.com" value={email} onChange={e => setEmail(e.target.value)} helperText={prefillEmail && email === prefillEmail ? 'Pre-filled from your account details.' : undefined} error={showError('email')} required data-id="element-216" />
              <Input label="Date of Birth" type="date" value={dob} onChange={e => setDob(e.target.value)} max={todayIso} helperText="You must be at least 18 years old." error={showError('dob')} required data-id="element-217" />
              <PhoneInput label="Mobile Phone Number" value={phone} onChange={setPhone} error={showError('phone')} helperText={prefillPhone && phone === prefillPhone ? 'Pre-filled from your account details.' : '9 or 10 digits, excluding the +60 country code.'} required data-id="element-218" />
            </FormGroup>
          </div>}

        {currentStep === 'identity' && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-219">
            {/* Identity Details */}
            <FormGroup title="Identity Details" data-id="element-220">
              <fieldset data-id="element-221">
                <legend className="block text-sm font-medium text-gray-900 mb-2" data-id="element-222">
                  Identity Document Type
                  <span className="text-red-600 ml-0.5" aria-hidden="true" data-id="element-223">
                    *
                  </span>
                  <span className="sr-only" data-id="element-224"> (required)</span>
                </legend>
                <div role="radiogroup" aria-label="Identity Document Type" className="grid grid-cols-2 gap-3" data-id="element-225">
                  <RadioCard label="National ID" description="MyKad / MyPR / Police / Military" selected={idMethod === 'national_id'} onClick={() => handleIdMethodChange('national_id')} data-id="element-226" />
                  <RadioCard label="Passport" description="Non-residents" selected={idMethod === 'passport'} onClick={() => handleIdMethodChange('passport')} data-id="element-227" />
                </div>
              </fieldset>

              <Input label={idMethod === 'national_id' ? 'ID Number' : 'Passport Number'} placeholder={idMethod === 'national_id' ? 'e.g. 900101-14-5678 (or G1234567 for Police/Military)' : 'e.g. A12345678'} value={idNumber} onChange={e => handleNricChange(e.target.value)} error={showError('idNumber')} inputMode={idMethod === 'national_id' && isCivilianNricFormat(idNumber) ? 'numeric' : 'text'} maxLength={idMethod === 'national_id' && isCivilianNricFormat(idNumber) ? 14 : 15} required data-id="element-228" />

              {idMethod === 'passport' && <Select label="Passport Issuing Country" placeholder="Select issuing country" options={PASSPORT_COUNTRIES} value={passportCountry} onChange={e => setPassportCountry(e.target.value)} error={showError('passportCountry')} required data-id="element-229" />}
            </FormGroup>
          </div>}

        {currentStep === 'address' && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-230">
            {/* Home Address */}
            <FormGroup title="Home Address" data-id="element-231">
              {hasBusinessAddress && <div className="rounded-lg bg-gray-50 border border-gray-200 p-3" data-id="element-232">
                  <Checkbox name="sameAsBusiness" checked={sameAsBusiness} onChange={e => setSameAsBusiness(e.target.checked)} label={<>Same as Registered Business Address</>} description="We'll reuse the address you provided in Business Details — saving you four fields of typing." data-id="element-233" />
                </div>}
              <div className={sameAsBusiness && hasBusinessAddress ? 'opacity-60 pointer-events-none select-none' : ''} aria-hidden={sameAsBusiness && hasBusinessAddress ? true : undefined} data-id="element-234">
                <AddressBlock value={address} onChange={setAddress} disabled={sameAsBusiness && hasBusinessAddress} required data-id="element-235" />
              </div>
              {showError('address') && !sameAsBusiness && <p className="text-sm text-red-700 font-medium" role="alert" data-id="element-236">
                  {showError('address')}
                </p>}
            </FormGroup>
          </div>}
      </form>

      {/* Sticky Footer */}
      <div className="sticky bottom-0 left-0 right-0 -mx-2 mt-12 bg-white/95 backdrop-blur-sm border-t border-gray-200" data-id="element-237">
        <div className="flex items-center justify-between py-4 px-2 gap-3" data-id="element-238">
          <Button variant="outline" onClick={handleBack} data-id="element-239">
            Back
          </Button>
          <Button onClick={handleNext} data-id="element-240">
            {currentStep === 'address' ? 'Save & Continue' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>;
}
function FormGroup({
  title,
  subtitle,
  children
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-241">
      <div className="mb-4" data-id="element-242">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide" data-id="element-243">
          {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-600 mt-1" data-id="element-244">{subtitle}</p>}
      </div>
      <div className="space-y-5" data-id="element-245">{children}</div>
    </div>;
}
function RadioCard({
  label,
  description,
  selected,
  onClick
}: {
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}) {
  return <button type="button" role="radio" aria-checked={selected} tabIndex={selected ? 0 : -1} onClick={onClick} className={`relative text-left p-4 rounded-lg border-2 transition-all min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8860B] focus-visible:ring-offset-2 ${selected ? 'border-[#FFCC00] bg-[#FFCC00]/10' : 'border-gray-300 bg-white hover:border-gray-500'}`} data-id="element-246">
      <div className="flex items-start gap-3" data-id="element-247">
        <span aria-hidden="true" className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#FFCC00]' : 'border-gray-500'}`} data-id="element-248">
          {selected && <span className="w-2 h-2 rounded-full bg-[#FFCC00]" data-id="element-249" />}
        </span>
        <div data-id="element-250">
          <div className="font-semibold text-sm text-gray-900" data-id="element-251">{label}</div>
          <div className="text-xs text-gray-600 mt-0.5" data-id="element-252">{description}</div>
        </div>
      </div>
    </button>;
}